import { Component, Input, Output, EventEmitter } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { toContextArray, firstContextRecord } from 'src/app/common/bhub-common-module/utils/context-array.util';
import { ViewManager, CalcOperand, InlineCalcOperand } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';

@Component({
  selector: 'bhub-list-calc-cell',
  standalone: false,
  templateUrl: './list-calc-cell.component.html',
  styleUrl: './list-calc-cell.component.scss'
})
export class ListCalcCellComponent extends BaseCellComponent {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  calculatedValue: number | null = null;
  formattedValue: string = '';
  label: string = '';
  showLabel: boolean = true;
  fontSize: number = 14;
  fontWeight: string = 'normal';
  hasOverride: boolean = false;

  private lastStoredValue: number | null = null;

  protected initializeCell(): void {
    this.loadConfig();
    this.calculate();
  }

  protected onCellOrDataChange(): void {
    this.loadConfig();
    this.calculate();
  }

  protected onCellUpdate(): void {
    this.loadConfig();
    this.calculate();
  }

  protected onDataSet(data: any): void {
    // Not used — data comes from context
  }

  override reloadData(): void {
    this.calculate();
  }

  private loadConfig(): void {
    this.label = this.getConfigValue('listCalcLabel', '');
    this.showLabel = this.getConfigValue('listCalcShowLabel', true);
    this.fontSize = this.getConfigValue('listCalcFontSize', 14);
    this.fontWeight = this.getConfigValue('listCalcFontWeight', 'normal');
  }

  private calculate(): void {
    // If there's a manual override from PDF edit mode, use it and skip recalculation
    const overrideValue = this.getConfigValue('pdfOverrideValue', undefined);
    if (overrideValue !== undefined) {
      this.hasOverride = true;
      this.formattedValue = overrideValue;
      this.calculatedValue = parseFloat(overrideValue) || 0;
      return;
    }
    this.hasOverride = false;

    const contextVariable: string = this.getConfigValue('listCalcContextVariable', '');
    const fieldName: string = this.getConfigValue('listCalcFieldName', '');
    const operation: string = this.getConfigValue('listCalcOperation', 'sum');
    const format: string = this.getConfigValue('listCalcFormat', 'number');
    const decimalPlaces: number = this.getConfigValue('listCalcDecimalPlaces', 2);

    if (!contextVariable || !fieldName || !this.viewManager) {
      this.calculatedValue = null;
      this.formattedValue = '';
      return;
    }

    const contextData = this.viewManager.contextData[contextVariable];
    if (!contextData) {
      this.calculatedValue = null;
      this.formattedValue = '';
      return;
    }

    // Get the array of items
    const items = toContextArray(contextData);
    if (!items) {
      this.calculatedValue = null;
      this.formattedValue = '';
      return;
    }

    // Collect per-row computed values
    const inlineOperands: InlineCalcOperand[] = this.getConfigValue('listCalcInlineOperands', []);
    const values: number[] = [];
    for (const item of items) {
      const rawValue = item[fieldName];

      // For COUNT, we just need the field to exist (any non-null value counts)
      if (operation === 'count') {
        if (rawValue !== null && rawValue !== undefined && String(rawValue) !== '') {
          values.push(1);
        }
        continue;
      }

      if (rawValue === null || rawValue === undefined) continue;
      let rowValue = this.parseNumericValue(rawValue);
      if (rowValue === null) continue;

      // Apply inline per-row operations
      if (inlineOperands && inlineOperands.length > 0) {
        for (const op of inlineOperands) {
          let opValue: number | null;
          if (op.sourceType === 'value') {
            opValue = op.literalValue ?? null;
          } else {
            const raw = item[op.fieldName];
            opValue = raw !== null && raw !== undefined ? this.parseNumericValue(raw) : null;
          }
          if (opValue === null) continue;

          switch (op.operator) {
            case '-': rowValue -= opValue; break;
            case '*': rowValue *= opValue; break;
            case '/': rowValue = opValue !== 0 ? rowValue / opValue : rowValue; break;
            default:  rowValue += opValue; break;
          }
        }
      }

      values.push(rowValue);
    }

    let result: number | null = null;

    if (values.length > 0) {
      switch (operation) {
        case 'average':
          result = values.reduce((a, b) => a + b, 0) / values.length;
          break;
        case 'count':
          result = values.length;
          break;
        case 'min':
          result = Math.min(...values);
          break;
        case 'max':
          result = Math.max(...values);
          break;
        case 'sum':
        default:
          result = values.reduce((a, b) => a + b, 0);
          break;
      }
    }

    // Apply post-aggregation operands
    const postOperands: CalcOperand[] = this.getConfigValue('listCalcPostOperands', []);
    if (result !== null && postOperands && postOperands.length > 0) {
      for (const operand of postOperands) {
        let numValue: number | null;

        if (operand.sourceType === 'value') {
          numValue = operand.literalValue ?? null;
        } else {
          const rawValue = this.getOperandValue(operand);
          if (rawValue === null || rawValue === undefined) continue;
          numValue = this.parseNumericValue(rawValue);
        }

        if (numValue === null) continue;

        switch (operand.operator) {
          case '-': result -= numValue; break;
          case '*': result *= numValue; break;
          case '/': result = numValue !== 0 ? result / numValue : result; break;
          default:  result += numValue; break;
        }
      }
    }

    this.calculatedValue = result;
    this.formattedValue = result !== null ? this.formatValue(result, format, decimalPlaces) : '';

    if (result !== null) {
      this.storeResult(result);
    }
  }

  private storeResult(result: number): void {
    const storeInContext = this.getConfigValue('listCalcStoreInContext', false);
    if (!storeInContext || !this.viewManager) return;

    if (this.lastStoredValue === result) return;
    this.lastStoredValue = result;

    const storeMode: string = this.getConfigValue('listCalcStoreMode', 'newVariable');

    if (storeMode === 'existingField') {
      const contextObject = this.getConfigValue('listCalcStoreContextObject', '');
      const fieldName = this.getConfigValue('listCalcStoreFieldName', '');
      if (!contextObject || !fieldName) return;

      const existingData = this.viewManager.contextData[contextObject];
      if (existingData && typeof existingData === 'object' && !Array.isArray(existingData)) {
        existingData[fieldName] = result;
        this.contextDataChange.emit({ [contextObject]: existingData });
      }
    } else {
      const contextVariable = this.getConfigValue('listCalcStoreContextVariable', '');
      const newVarFieldName = this.getConfigValue('listCalcNewVariableFieldName', '');
      if (!contextVariable || !newVarFieldName) return;
      this.contextDataChange.emit({ [contextVariable]: { [newVarFieldName]: result } });
    }
  }

  private parseNumericValue(raw: any): number | null {
    if (typeof raw === 'number') return isNaN(raw) ? null : raw;
    if (typeof raw === 'string') {
      const cleaned = raw.replace(/[$€£¥,\s]/g, '');
      if (cleaned === '') return null;
      const num = Number(cleaned);
      return isNaN(num) ? null : num;
    }
    return null;
  }

  private getOperandValue(operand: CalcOperand): any {
    if (!this.viewManager?.contextData || !operand.contextVariable || !operand.fieldName) return null;

    const contextData = this.viewManager.contextData[operand.contextVariable];
    if (!contextData) return null;

    const record = firstContextRecord(contextData);
    return record ? record[operand.fieldName] : null;
  }

  private formatValue(value: number, format: string, decimalPlaces: number): string {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency', currency: 'USD',
          minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces
        }).format(value);
      case 'percent':
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces
        }).format(value);
      default:
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces
        }).format(value);
    }
  }
}
